// Store delle IMPOSTAZIONI + tema
import { create } from 'zustand';
import type { AppSettings } from '@/types';
import { getDatabase } from '@/lib/db';
import { DEFAULT_SETTINGS } from '@/utils/constants';

interface SettingsState {
  settings: AppSettings;
  loaded: boolean;
  dbReady: boolean;
  load: () => Promise<void>;
  save: (patch: Partial<AppSettings>) => Promise<void>;
  setDbReady: (v: boolean) => void;
  applyTheme: (theme: AppSettings['theme']) => void;
}

function detectSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyThemeClass(theme: AppSettings['theme']) {
  if (typeof document === 'undefined') return;
  const effective = theme === 'system' ? detectSystemTheme() : theme;
  document.documentElement.classList.toggle('dark', effective === 'dark');
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', effective === 'dark' ? '#0f172a' : '#6366f1');
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  settings: { ...DEFAULT_SETTINGS },
  loaded: false,
  dbReady: false,

  load: async () => {
    try {
      const db = getDatabase();
      const s = await db.getSettings();
      const merged: AppSettings = { ...DEFAULT_SETTINGS, ...(s ?? {}) };
      set({ settings: merged, loaded: true });
      applyThemeClass(merged.theme);
    } catch (e) {
      console.error('[settings] load error', e);
      set({ loaded: true });
    }
  },

  save: async (patch) => {
    const current = get().settings;
    const next: AppSettings = { ...current, ...patch };
    const db = getDatabase();
    await db.upsertSettings(next);
    set({ settings: next });
    if (patch.theme) applyThemeClass(next.theme);
  },

  setDbReady: (v) => set({ dbReady: v }),

  applyTheme: (theme) => {
    applyThemeClass(theme);
  },
}));
